'use client'

import React from 'react'
import { useState, useEffect, useRef } from 'react'
import { Menu, X } from 'lucide-react'

interface NavbarProps {
  isMobile?: boolean
  isHomePage?: boolean
}

const navItems = [
  { id: 'community', label: 'Community' },
  { id: 'courses', label: 'Courses', href: 'https://www.aibuilderclub.com/courses' },
  { id: 'tools', label: 'Tools' },
  { id: 'skills', label: 'Skills', href: '/skills' },
  { id: 'ai-builder-pack', label: 'AI Builder Pack' },
  { id: 'free-resources', label: 'Free Resources', href: '/blog' },
  { id: 'pricing', label: 'Pricing' }
]

export default function Navbar({ isMobile = false, isHomePage = true }: NavbarProps) {
  const [activeTab, setActiveTab] = useState('community')
  const [isMenuOpen, setIsMenuOpen] = useState(false)
  const isScrollingRef = useRef(false)

  const handleNavClick = (item: typeof navItems[number]) => {
    setIsMenuOpen(false)
    if (item.href) {
      window.location.href = item.href
      return
    }
    if (isHomePage) {
      setActiveTab(item.id)
      const element = document.getElementById(item.id)
      if (element) {
        isScrollingRef.current = true
        element.scrollIntoView({ behavior: 'smooth' })
        setTimeout(() => { isScrollingRef.current = false }, 1000)
      }
    } else {
      window.location.href = `/#${item.id}`
    }
  }

  useEffect(() => {
    if (!isHomePage) return

    const handleScroll = () => {
      if (isScrollingRef.current) return

      for (const item of navItems) {
        if (item.href) continue
        const element = document.getElementById(item.id)
        if (element) {
          const { top, bottom } = element.getBoundingClientRect()
          if (top <= 100 && bottom > 100) {
            setActiveTab(item.id)
            break
          }
        }
      }
    }

    window.addEventListener('scroll', handleScroll)
    return () => window.removeEventListener('scroll', handleScroll)
  }, [isHomePage])

  return (
    <div className="fixed top-0 left-0 right-0 z-50 bg-black/50 backdrop-blur-md">
      <nav className="max-w-6xl mx-auto px-4 py-4">
        {isMobile ? (
          <div className="flex justify-between items-center">
            <button 
              className="text-white p-2"
              onClick={() => setIsMenuOpen(!isMenuOpen)}
            >
              {isMenuOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
            {isMenuOpen && (
              <div className="absolute top-full left-0 right-0 bg-zinc-900 py-4">
                <ul className="flex flex-col space-y-2">
                  {navItems.map((item) => (
                    <li key={item.id} className="px-4">
                      {item.href ? (
                        <a
                          href={item.href}
                          className="block w-full text-left py-2 text-sm font-medium text-white/60 hover:text-white/90 transition-colors"
                        >
                          {item.label}
                        </a>
                      ) : (
                        <button 
                          className={`w-full text-left py-2 text-sm font-medium transition-colors ${
                            isHomePage && activeTab === item.id 
                              ? 'text-white' 
                              : 'text-white/60 hover:text-white/90'
                          }`}
                          onClick={() => handleNavClick(item)}
                        >
                          {item.label}
                        </button>
                      )}
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        ) : (
          <div className="flex justify-center">
            <div className="bg-zinc-900/80 rounded-full px-2 lg:px-4 py-1.5">
              <ul className="flex items-center space-x-0.5 lg:space-x-2">
                {navItems.map((item) => {
                  const isActive = isHomePage && !item.href && activeTab === item.id
                  const baseClass = `relative inline-flex items-center px-3 lg:px-5 py-2.5 rounded-full text-xs lg:text-sm font-medium transition-colors whitespace-nowrap`
                  if (item.href) {
                    return (
                      <li key={item.id}>
                        <a
                          href={item.href}
                          className={`${baseClass} text-white/60 hover:text-white/90`}
                        >
                          {item.label}
                        </a>
                      </li>
                    )
                  }
                  return (
                    <li key={item.id}>
                      <button
                        className={`${baseClass} ${
                          isActive ? 'text-white' : 'text-white/60 hover:text-white/90'
                        }`}
                        onClick={() => handleNavClick(item)}
                      >
                        {item.label}
                        {isActive && (
                          <span className="absolute bottom-0 left-1/2 -translate-x-1/2 w-10 h-0.5 bg-white"></span>
                        )}
                      </button>
                    </li>
                  )
                })}
              </ul>
            </div>
          </div>
        )}
      </nav>
    </div>
  )
}
